import type {
  IAuthStore,
  IQueryClient,
  IWorkspaceStore,
  TQueryClientFactory,
  WorkspaceData,
} from "@workspace/types";
import { action, makeAutoObservable, reaction, runInAction } from "mobx";
import { injectable, inject } from "inversify";
import { ServiceID } from "./symbols";
import {
  getOrCreate,
  renameWorkspace as renameWorkspaceApi,
  setStaleWorkspaceHandler,
  setWorkspaceIdProvider,
  updateActiveWorkspace,
} from "@workspace/api";

@injectable()
export class WorkspaceStore implements IWorkspaceStore {
  private workspacesQuery: IQueryClient<WorkspaceData[]>;
  private _activeWorkspaceId: string | null =
    typeof window !== "undefined"
      ? localStorage.getItem("activeWorkspaceId")
      : null;
  private _recoveredStaleIds = new Set<string>();

  constructor(
    @inject(ServiceID.QueryClientFactory)
    private queryClientFactory: TQueryClientFactory,
    @inject(ServiceID.AuthStore)
    private authStore: IAuthStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.workspacesQuery = this.queryClientFactory<WorkspaceData[]>(() => ({
      queryKey: ["workspaces"],
      queryFn: () =>
        getOrCreate({
          name: this.authStore.user?.email
            ? `${this.authStore.user.email} workspace`
            : "Default workspace",
        }),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
      enabled: !!(authStore.isAuthenticated && authStore.user?.id),
    }));

    reaction(
      () => this.authStore.isAuthenticated,
      action((isAuthenticated: boolean, wasAuthenticated: boolean) => {
        if (wasAuthenticated && !isAuthenticated) {
          this._activeWorkspaceId = null;
          if (typeof window !== "undefined") {
            localStorage.removeItem("activeWorkspaceId");
          }
          this.workspacesQuery.queryClient.clear();
        }
      }),
    );

    setWorkspaceIdProvider(() => this.activeWorkspaceId);
    setStaleWorkspaceHandler((staleId) => this.handleStaleWorkspace(staleId));
  }

  get workspaces(): WorkspaceData[] | undefined {
    return this.workspacesQuery.result.data;
  }

  get activeWorkspace(): WorkspaceData | undefined {
    const data = this.workspacesQuery.result.data;
    if (!data) return undefined;

    const localId = this._activeWorkspaceId;
    const localMatch = localId
      ? data.find((wd) => wd.workspace.id === localId)
      : undefined;

    if (localMatch) {
      return localMatch;
    }

    // localStorage id is absent or no longer in the membership list — we must
    // consult the server default. Reading currentUser here (through AuthStore's
    // getter) is what registers this computed as an observer of the underlying
    // query's MobX atom and triggers the lazy fetch.
    const serverDefaultId = this.authStore.currentUser?.active_workspace_id;
    const serverMatch = serverDefaultId
      ? data.find((wd) => wd.workspace.id === serverDefaultId)
      : undefined;

    return serverMatch ?? data[0];
  }

  get activeWorkspaceId(): WorkspaceData["workspace"]["id"] | undefined {
    return this.activeWorkspace?.workspace.id ?? undefined;
  }

  get isWorkspaceReady(): boolean {
    if (!this.workspacesQuery.result.isSuccess) return false;

    const data = this.workspacesQuery.result.data;
    if (!data) return false;
    if (data.length === 0) return false;

    const localId = this._activeWorkspaceId;
    const localMatch = localId
      ? data.find((wd) => wd.workspace.id === localId)
      : undefined;

    // If localStorage holds a valid, present workspace id, that's authoritative
    // and we don't need to wait for /users/me.
    if (localMatch) return true;

    // Otherwise we're waiting on the server default. Block readiness until the
    // current-user query settles (success or error) so consumers never see the
    // wrong workspace id in the window between workspacesQuery landing and
    // /users/me completing. On error we fall back to data[0], which is fine —
    // the important thing is we don't deadlock forever.
    return this.authStore.isCurrentUserSettled;
  }

  get isLoading() {
    return this.workspacesQuery.result.isLoading;
  }

  async setActiveWorkspace(id: string): Promise<void> {
    const previousId = this._activeWorkspaceId;
    this._activeWorkspaceId = id;
    localStorage.setItem("activeWorkspaceId", id);
    try {
      await updateActiveWorkspace(id);
    } catch (e) {
      this._activeWorkspaceId = previousId;
      if (previousId) {
        localStorage.setItem("activeWorkspaceId", previousId);
      } else {
        localStorage.removeItem("activeWorkspaceId");
      }
      throw e;
    }
  }

  async renameWorkspace(id: string, name: string): Promise<void> {
    await renameWorkspaceApi(id, name);
    const current = this.workspacesQuery.result.data;
    if (!current) return;
    const updated = current.map((wd) =>
      wd.workspace.id === id
        ? { ...wd, workspace: { ...wd.workspace, name } }
        : wd,
    );
    this.workspacesQuery.queryClient.setQueryData(
      ["workspaces"],
      updated,
    );
  }

  handleStaleWorkspace(staleId: string): void {
    if (this._recoveredStaleIds.has(staleId)) {
      return;
    }
    this._recoveredStaleIds.add(staleId);
    if (this._activeWorkspaceId === staleId) {
      this._activeWorkspaceId = null;
      localStorage.removeItem("activeWorkspaceId");
    }
    void this.workspacesQuery.refetch().finally(() => {
      runInAction(() => {
        this._recoveredStaleIds.delete(staleId);
      });
    });
  }

  dispose(): void {
    this.workspacesQuery.queryClient.clear();
  }
}
